import { Infinity as InfinityIcon, type LucideIcon } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface BillingUsageMeterProps {
  label: string;
  used: number;
  limit?: number | null;
  icon: LucideIcon;
}

export default function BillingUsageMeter({ label, used, limit, icon: Icon }: BillingUsageMeterProps) {
  const unlimited = limit === null;
  const percent = typeof limit === "number"
    ? Math.max(0, Math.min(100, limit > 0 ? used / limit * 100 : used > 0 ? 100 : 0))
    : null;
  const format = (value: number) => value.toLocaleString("en-SG");

  return (
    <article className="min-w-0 rounded-lg border border-border bg-card p-4 shadow-sm" data-testid="billing-usage-card">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-sm font-medium text-muted-foreground">{label}</h3>
      </div>
      <p className="mt-3 text-sm tabular-nums text-muted-foreground">
        <strong className="font-semibold text-foreground">{format(used)}</strong>/{unlimited ? "Unlimited" : limit !== undefined ? format(limit) : "—"} used
      </p>
        {unlimited ? (
          <div role="img" aria-label={`${label}: unlimited allowance`} className="relative mt-1 flex h-5 items-center justify-center">
            <div className="absolute inset-x-0 h-1.5 rounded-full bg-muted" />
            <span className="relative rounded-full bg-card px-1.5 text-muted-foreground"><InfinityIcon className="h-4 w-4" aria-hidden="true" /></span>
          </div>
        ) : percent !== null ? (
          <div className="mt-1 flex h-5 items-center">
            <Progress
              aria-label={`${label} usage`}
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuetext={`${format(used)} of ${format(limit!)} used`}
              value={percent}
              style={{ backgroundImage: "repeating-linear-gradient(120deg, transparent 0px, transparent 2px, hsl(var(--border)) 2px, hsl(var(--border)) 4px)" }}
              className="h-1.5 bg-muted [&>div]:bg-muted-foreground"
            />
          </div>
        ) : (
          <div className="mt-1 flex h-5 items-center" aria-hidden="true"><div className="h-1.5 w-full rounded-full bg-muted" /></div>
        )}
    </article>
  );
}
